import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  TablePagination,
  IconButton
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import { toast } from "react-toastify";
import { createUnidades, deleteUnidades, getUnidadesPorEmpresa, updateUnidades } from '../../services/unidades/unidadesData';
import { useAuth } from "../../context/AuthContext";

const TablaUnidades = () => {
  const [datos, setDatos] = useState([]);
  const [formulario, setFormulario] = useState({ uni_nombre: '', });
  const [modalOpen, setModalOpen] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [modificarIndex, setModificarIndex] = useState(-1);
  const { user } = useAuth();
  const emp_codigo = user?.emp_codigo;
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (emp_codigo) {
          const unidadesData = await getUnidadesPorEmpresa(emp_codigo);
          setDatos(Array.isArray(unidadesData) ? unidadesData : []);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setDatos([]);
      }
    };

    fetchData();
  }, [emp_codigo]);

  const handleBusquedaChange = (event) => {
    setBusqueda(event.target.value);
    setPage(0);
  };

  const handleInsert = () => {
    setModificarIndex(-1);
    setFormulario({
      uni_nombre: '',
    });
    setModalOpen(true);
  };

  const handleFormChange = (event) => {
    setFormulario({
      ...formulario,
      [event.target.name]: event.target.value,
    });
  };

  const handleGuardar = async () => {
    if (!formulario.uni_nombre.trim()) {
      toast.warning("El nombre de la unidad es obligatorio");
      return;
    }
    try {
      if (modificarIndex !== -1) {
        await updateUnidades(modificarIndex, {
          ...formulario,
          emp_codigo,
        });
        toast.success("Unidad actualizada correctamente");
      } else {
        await createUnidades({
          ...formulario,
          emp_codigo,
        });
        toast.success("Unidad creada correctamente");
      }
      const unidadesData = await getUnidadesPorEmpresa(emp_codigo);
      setDatos(Array.isArray(unidadesData) ? unidadesData : []);
      setModalOpen(false);
      setFormulario({
        uni_nombre: "",
      });
    } catch (error) {
      console.error("Error al guardar la unidad:", error);
      const mensaje =
        error?.response?.data?.message ||
        error?.message ||
        "No se pudo guardar la unidad.";
      toast.error(mensaje);
    }
  };

  const handleCancelar = () => {
    setModalOpen(false);
    setFormulario({
      uni_nombre: '',
    });
  };

  const handleModificar = (index) => {
    const unidad = datos[index];
    setModificarIndex(unidad.uni_codigo);
    setFormulario({
      uni_nombre: unidad.uni_nombre,
    });
    setModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const codigoToDelete = datos[deleteIndex].uni_codigo;
      await deleteUnidades(codigoToDelete);
      const unidadesData = await getUnidadesPorEmpresa(emp_codigo);
      setDatos(Array.isArray(unidadesData) ? unidadesData : []);
      toast.success("Unidad eliminada correctamente");
      setConfirmOpen(false);
      setDeleteIndex(null);
    } catch (error) {
      console.error("Error al eliminar la unidad:", error);
      const mensaje =
        error?.message ||
        error?.response?.data?.message ||
        "No se puede eliminar la unidad porque está siendo utilizada en el sistema.";
      if (
        error?.status === 400 ||
        mensaje.toLowerCase().includes("no se puede eliminar") ||
        mensaje.toLowerCase().includes("asociada") ||
        error?.response?.data?.errno === 1451
      ) {
        setErrorMessage(mensaje);
        setErrorModalOpen(true);
      } else {
        toast.error(mensaje);
      }
      setConfirmOpen(false);
      setDeleteIndex(null);
    }
  };

  const handleCancelDelete = () => {
    setConfirmOpen(false);
    setDeleteIndex(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const datosFiltrados = datos.filter((dato) =>
    dato.uni_nombre && dato.uni_nombre.toLowerCase().includes(busqueda.toLowerCase())
  );

  return (
    <Box>
      {/* Toolbar */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          borderBottom: '1px solid #e0e0e0'
        }}
      >
        <TextField
          variant="outlined"
          size="small"
          placeholder="Buscar unidad..."
          value={busqueda}
          onChange={handleBusquedaChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: { xs: '100%', sm: 300 } }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleInsert}
          sx={{
            backgroundColor: '#001F3F',
            color: '#fff',
            '&:hover': { backgroundColor: '#003366' },
            textTransform: 'none',
            fontWeight: 500
          }}
        >
          Nueva Unidad
        </Button>
      </Box>

      <TableContainer>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 600, color: '#001F3F' }}>Nombre</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#001F3F' }} align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {datosFiltrados.length === 0 ? (
              <TableRow>
                <TableCell colSpan={2} align="center" sx={{ py: 3 }}>
                  <Typography variant="body1" sx={{ color: '#666' }}>
                    {datos.length === 0 ? "No hay unidades registradas." : "No se encontraron resultados."}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              datosFiltrados
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((dato) => (
                  <TableRow key={dato.uni_codigo} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: '#001F3F' }}>
                        {dato.uni_nombre}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <IconButton
                          onClick={() => handleModificar(datos.findIndex(d => d.uni_codigo === dato.uni_codigo))}
                          size="small"
                          sx={{ color: "#666" }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          onClick={() => {
                            const originalIndex = datos.findIndex(d => d.uni_codigo === dato.uni_codigo);
                            setDeleteIndex(originalIndex);
                            setConfirmOpen(true);
                          }}
                          size="small"
                          sx={{ color: "#d32f2f" }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={datosFiltrados.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Filas por página:"
      />

      {/* Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleCancelar}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 2, fontWeight: 600, color: '#001F3F', fontSize: '1.25rem' }}>
          {modificarIndex !== -1 ? 'Modificar Unidad' : 'Nueva Unidad'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ mt: 2 }}>
            <TextField
              label="Nombre de la Unidad"
              name="uni_nombre"
              fullWidth
              value={formulario.uni_nombre}
              onChange={handleFormChange}
              variant="outlined"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCancelar} sx={{ color: '#666' }}>Cancelar</Button>
          <Button
            onClick={handleGuardar}
            variant="contained"
            sx={{
              backgroundColor: '#001F3F',
              color: '#fff',
              '&:hover': { backgroundColor: '#003366' }
            }}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmación de eliminación */}
      <Dialog
        open={confirmOpen}
        onClose={handleCancelDelete}
      >
        <DialogTitle sx={{ fontWeight: 600, color: '#001F3F', fontSize: '1.25rem' }}>
          Confirmar Eliminación
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#666' }}>
            ¿Está seguro que desea eliminar esta unidad?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCancelDelete} sx={{ color: '#666' }}>Cancelar</Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            sx={{
              backgroundColor: '#d32f2f',
              color: '#fff',
              '&:hover': { backgroundColor: '#c62828' }
            }}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de error */}
      <Dialog
        open={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, color: '#001F3F', fontSize: '1.25rem' }}>
          No se puede eliminar
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <Typography variant="body1" sx={{ color: '#666', mb: 2 }}>
              {errorMessage || 'Esta unidad está siendo utilizada y no puede eliminarse.'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#999' }}>
              Antes de eliminarla, actualiza o elimina las relaciones donde se usa (activos, vulnerabilidades, etc.).
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setErrorModalOpen(false)}
            variant="contained"
            sx={{ backgroundColor: '#001F3F', color: '#fff', '&:hover': { backgroundColor: '#003366' } }}
          >
            Entendido
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TablaUnidades;
